
import { useState, FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d6ilkm6fjdhm2lq4sscg';

export default function Signup() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    product: '',
    message: '',
  });
  const [privacy, setPrivacy] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'message' && value.length > 500) return;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      alert('이름을 입력해주세요.');
      return;
    }
    if (!formData.phone.trim()) {
      alert('전화번호를 입력해주세요.');
      return;
    }
    if (!privacy) {
      alert('개인정보 처리방침에 동의해주세요.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    const body = new URLSearchParams();
    body.append('name', formData.name);
    body.append('phone', formData.phone);
    if (formData.product) body.append('product', formData.product);
    if (formData.message) body.append('message', formData.message);
    body.append('privacy', '동의');

    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (res.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', phone: '', product: '', message: '' });
        setPrivacy(false);
        setTimeout(() => setSubmitStatus('idle'), 5000);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="signup" className="py-14 md:py-24 bg-gradient-to-br from-blue-600 to-blue-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 md:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-white mb-3 md:mb-4 leading-tight">
            나에게 꼭 맞는 골프 멤버십,<br />
            전문가와 상담하세요!
          </h2>
          <p className="text-sm sm:text-base md:text-xl text-white/90 max-w-3xl mx-auto">
            이용 패턴에 따른 맞춤형 설계로 혜택은 높이고 부담은 줄여드립니다.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl md:rounded-3xl p-6 sm:p-8 md:p-14 shadow-2xl">
            <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-6 md:mb-10 text-center">상담 신청하기</h3>

            <form data-readdy-form id="consultation-form" onSubmit={handleSubmit} className="space-y-5 md:space-y-7">
              {/* 이름 */}
              <div>
                <label htmlFor="cons-name" className="block text-sm md:text-base font-semibold text-gray-700 mb-1.5">
                  이름 <span className="text-red-500">*</span>
                </label>
                <input
                  id="cons-name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="이름을 입력하세요"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* 전화번호 */}
              <div>
                <label htmlFor="cons-phone" className="block text-sm md:text-base font-semibold text-gray-700 mb-1.5">
                  전화번호 <span className="text-red-500">*</span>
                </label>
                <input
                  id="cons-phone"
                  name="phone"
                  type="text"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="010-1234-5678"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* 관심 상품 */}
              <div>
                <label htmlFor="cons-product" className="block text-sm md:text-base font-semibold text-gray-700 mb-1.5">
                  관심 상품
                </label>
                <select
                  id="cons-product"
                  name="product"
                  value={formData.product}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer bg-white"
                >
                  <option value="">선택하세요</option>
                  <option value="개인 (3,300만원)">개인 (3,300만원)</option>
                  <option value="개인 1+1 (6,300만원)">개인 1+1 (6,300만원)</option>
                  <option value="법인 (1.29억원)">법인 (1.29억원)</option>
                  <option value="법인 고급형 (2.85억원)">법인 고급형 (2.85억원)</option>
                  <option value="법인 프리미엄 (5.19억원)">법인 프리미엄 (5.19억원)</option>
                </select>
              </div>

              {/* 문의사항 */}
              <div>
                <label htmlFor="cons-message" className="block text-sm md:text-base font-semibold text-gray-700 mb-1.5">
                  문의사항
                </label>
                <textarea
                  id="cons-message"
                  name="message"
                  rows={5}
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="문의하실 내용을 입력하세요 (최대 500자)"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
                <p className="text-right text-xs text-gray-400 mt-1">{formData.message.length}/500</p>
              </div>

              {/* 개인정보 동의 */}
              <div className="flex items-start gap-2 md:gap-3">
                <input
                  id="cons-privacy"
                  name="privacy"
                  type="checkbox"
                  checked={privacy}
                  onChange={(e) => setPrivacy(e.target.checked)}
                  className="mt-0.5 w-4 h-4 md:w-5 md:h-5 accent-blue-600 cursor-pointer"
                />
                <label htmlFor="cons-privacy" className="text-xs md:text-sm text-gray-600 cursor-pointer leading-relaxed">
                  개인정보 처리방침 및 이용 약관에 동의합니다. <span className="text-red-500">*</span>
                </label>
              </div>

              {/* 제출 버튼 */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-4 md:py-5 rounded-xl md:rounded-2xl text-base md:text-xl transition-colors whitespace-nowrap cursor-pointer shadow-lg"
              >
                {isSubmitting ? '전송 중...' : '상담 신청하기'}
              </button>

              {/* 결과 메시지 */}
              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl text-sm md:text-base text-center">
                  ✅ 상담 신청이 완료되었습니다. 빠른 시일 내에 연락드리겠습니다.
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm md:text-base text-center">
                  ❌ 전송 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
